import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import moment from 'moment'

import { categoriesGroup, manageGroup } from '../../routers'
import { createGroup, inviteToGroup } from '../../api/group'
import { apiError, getCloseOptions, leaveGroup } from '../../api/account'
import { setGroups } from '../../redux/actions/groups'
import { ROLE_ADMIN, ROLE_OWNER } from '../../utils/constants'

import styles from './AccountSettings.module.css'

const ROLE_TEXT = { [ROLE_OWNER]: 'You own this group', [ROLE_ADMIN]: 'Admin' }

// "Leave group": the recipes you shared stay with the family unless you take them.
// An owner picks who looks after the group next (longest-standing member by default).
const LeaveDialog = ({ group, onDone, onCancel }) => {
  const [keep, setKeep] = useState(true)
  const [members, setMembers] = useState(group.role === ROLE_OWNER ? null : [])
  const [newOwner, setNewOwner] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (group.role !== ROLE_OWNER) return
    getCloseOptions()
      .then(({ data }) => {
        const owned = data.owned_groups.find((g) => g.id === group.id)
        const list = owned?.members || []
        setMembers(list)
        setNewOwner(list[0] ? String(list[0].id) : '')
      })
      .catch(() => setMembers([]))
  }, [group.id, group.role])

  const leave = () => {
    setBusy(true)
    setError('')
    leaveGroup(group.id, {
      keep_recipes: keep,
      ...(newOwner ? { new_owner_id: Number(newOwner) } : {}),
    })
      .then(() => onDone(group.id))
      .catch((err) => {
        setError(apiError(err, 'We could not take you out of this group. Please try again.'))
        setBusy(false)
      })
  }

  const alone = Array.isArray(members) && group.role === ROLE_OWNER && !members.length

  return (
    <div className={styles.alert} role="dialog" aria-label={`Leave ${group.name}`}>
      <b>Leave {group.name}?</b>
      {alone ? (
        <p className={styles.small}>
          You are the only member, so the group will be removed. Your recipes stay in your account.
        </p>
      ) : (
        <>
          <p className={styles.small}>What should happen to the recipes you shared here?</p>
          <div className={styles.list}>
            <label className={styles.check}>
              <input
                type="radio"
                name={`keep-${group.id}`}
                checked={keep}
                onChange={() => setKeep(true)}
              />
              <span>
                <b>Leave them with the family.</b> They stay in the group, still credited to you.
              </span>
            </label>
            <label className={styles.check}>
              <input
                type="radio"
                name={`keep-${group.id}`}
                checked={!keep}
                onChange={() => setKeep(false)}
              />
              <span>
                <b>Take them with me.</b> They come out of this group but stay in your account.
              </span>
            </label>
          </div>
        </>
      )}
      {group.role === ROLE_OWNER && members?.length ? (
        <label className={styles.label} style={{ marginTop: 14 }}>
          Who looks after {group.name} after you?
          <select
            className={styles.input}
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.fullname}
                {m.joined_at ? ` (member since ${moment(m.joined_at).year()})` : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className={styles.row} style={{ marginTop: 14 }}>
        <button className={styles.btnDanger} disabled={busy || members === null} onClick={leave}>
          Leave group
        </button>
        <button className={styles.btn} onClick={onCancel}>
          Stay in the group
        </button>
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  )
}

const GroupsSection = () => {
  const dispatch = useDispatch()
  const groups = useSelector((state) => state.groups?.data) || []
  const [leaving, setLeaving] = useState(null)
  const [newName, setNewName] = useState('')
  const { hash } = useLocation()
  const [creating, setCreating] = useState(hash === '#create-group')
  const [invite, setInvite] = useState({ email: '', group_id: '' })
  const [message, setMessage] = useState({ text: '', ok: true })
  const [copied, setCopied] = useState(false)

  const inviteGroup = groups.find((g) => String(g.id) === String(invite.group_id)) || groups[0]

  const create = (e) => {
    e.preventDefault()
    if (!newName.trim()) return
    createGroup({ name: newName.trim() })
      .then(({ data }) => {
        dispatch(setGroups([...groups, data]))
        setNewName('')
        setCreating(false)
        setMessage({ text: `${data.name} is ready. Invite your family below.`, ok: true })
      })
      .catch((err) =>
        setMessage({ text: apiError(err, 'We could not create that group.'), ok: false })
      )
  }

  const sendInvite = (e) => {
    e.preventDefault()
    const email = invite.email.trim()
    if (!/^\S+@\S+\.\S+$/.test(email)) return setMessage({ text: 'Enter a valid email', ok: false })
    inviteToGroup({ emails: [{ email }], group_id: inviteGroup.id })
      .then(() => {
        setInvite((i) => ({ ...i, email: '' }))
        setMessage({ text: `Invite sent to ${email}.`, ok: true })
      })
      .catch((err) =>
        setMessage({ text: apiError(err, 'We could not send that invite.'), ok: false })
      )
  }

  const copyLink = () => {
    navigator.clipboard?.writeText(inviteGroup.inviteLink).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const left = (id) => {
    dispatch(setGroups(groups.filter((g) => g.id !== id)))
    setLeaving(null)
    setMessage({ text: 'You have left the group.', ok: true })
  }

  return (
    <section id="groups" className={styles.card} aria-labelledby="groups-title">
      <div className={styles.cardHead}>
        <h2 id="groups-title" className={styles.h2}>
          Groups and invites
        </h2>
        <button className={styles.btnLink} onClick={() => setCreating((c) => !c)}>
          Create a group
        </button>
      </div>
      <p className={styles.lead}>The families you share recipes with.</p>

      {creating ? (
        <form
          onSubmit={create}
          className={styles.row}
          style={{ marginBottom: 16, alignItems: 'flex-end' }}
        >
          <label className={`${styles.label} ${styles.grow}`}>
            New group name
            <input
              className={styles.input}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </label>
          <button className={styles.btnDark}>Create group</button>
        </form>
      ) : null}

      <div className={styles.list}>
        {!groups.length ? <p className={styles.small}>You are not in any groups yet.</p> : null}
        {groups.map((g) => (
          <div key={g.id}>
            <div className={styles.listRow}>
              <div className={styles.grow}>
                <Link to={categoriesGroup(g.slug, g.id)}>
                  <b>{g.name}</b>
                </Link>
                <div className={styles.small}>{ROLE_TEXT[g.role] || 'Member'}</div>
              </div>
              {g.role === ROLE_OWNER || g.role === ROLE_ADMIN ? (
                <Link to={manageGroup(g.id)} className={styles.btnLink}>
                  Manage members
                </Link>
              ) : null}
              <button className={styles.btnLinkDanger} onClick={() => setLeaving(g)}>
                Leave group
              </button>
            </div>
            {leaving?.id === g.id ? (
              <LeaveDialog group={g} onDone={left} onCancel={() => setLeaving(null)} />
            ) : null}
          </div>
        ))}
      </div>

      {groups.length ? (
        <>
          <h3 className={styles.h3}>Invite someone</h3>
          <form onSubmit={sendInvite} className={styles.grid3} style={{ alignItems: 'end' }}>
            <label className={styles.label}>
              Invite by email
              <input
                className={styles.input}
                type="email"
                value={invite.email}
                onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))}
              />
            </label>
            <label className={styles.label}>
              To group
              <select
                className={styles.input}
                value={inviteGroup?.id || ''}
                onChange={(e) => setInvite((i) => ({ ...i, group_id: e.target.value }))}
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            <button className={styles.btnDark}>Send invite</button>
          </form>
          {inviteGroup?.inviteLink ? (
            <p className={styles.small}>
              Or share your private invite link for {inviteGroup.name}:{' '}
              <button
                type="button"
                className={styles.btnLink}
                style={{ minHeight: 0 }}
                onClick={copyLink}
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </p>
          ) : null}
        </>
      ) : null}
      {message.text ? (
        <p className={message.ok ? styles.saved : styles.error} role="status">
          {message.text}
        </p>
      ) : null}
    </section>
  )
}

export default GroupsSection
